/* eslint camelcase: 0 */
import { config } from '@v4/config';
import { doRequest, request } from '@v4/http/request';
import { authTokenService } from '@v4/services/auth-token-service';
import { eventService } from '@v4/services/event-service';
import { authenticationStatuses } from '@v4/login/config';

const { API_URL } = config;

/**
 * Create a user POSTing the relative data to the server.
 *
 * @param email
 * @param name
 * @param surname
 * @param middle_surname
 * @param phone
 * @param zip_code
 * @param privacy
 * @param privacy_flags
 * @returns {Promise<*>}
 */
export const userCreate = async ({
    email,
    name,
    surname,
    middle_surname = '',
    phone = '',
    zip_code = '',
    privacy = true,
    privacy_flags = {
        profiling: false,
        marketing: false,
        third_parties: false,
    },
}) => {
    const url = `${API_URL}/garagek/v1/user/create`;
    const user = {
        email,
        name,
        surname,
        middle_surname,
        phone,
        zip_code,
        privacy,
        privacy_flags,
    };

    const response = await doRequest(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json; charset=UTF-8',
        },
        body: JSON.stringify(user),
    });

    return await response.json();
};

/**
 * Confirm a user with a pending registration process.
 *
 * @param key
 * @param password
 * @returns {Promise<*>}
 */
export const userConfirm = async (key, password) => {
    const url = `${API_URL}/garagek/v1/user/confirm`;

    const response = await doRequest(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json; charset=UTF-8',
        },
        body: JSON.stringify({ key, password }),
    });

    return await response.json();
};

/**
 * Authenticate user via auth token
 *
 * @param {string} token
 */
export const userAuthenticate = (token) => {
    authTokenService.setToken(token);
    eventService.emit(config.events.EVENT_GARAGE_AUTHENTICATION_STATUS_CHANGE, {
        status: authenticationStatuses.STATUS_AUTHENTICATED,
    });
    eventService.emit(config.events.EVENT_GARAGE_AUTHENTICATION_LOGIN);
};

/**
 * Given the relative credential authenticate the user against the server.
 *
 * @param email
 * @param password
 * @returns {Promise<*>}
 */
export const userLogin = async (email = '', password = '') => {
    const url = `${API_URL}/garagek/v1/user/login`;

    const response = await doRequest(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json; charset=UTF-8',
        },
        body: JSON.stringify({ email, password }),
    });

    const jsonResponse = await response.json();

    userAuthenticate(jsonResponse.token);

    return jsonResponse;
};

/**
 * Logout authenticated user
 *
 * @returns {Promise<boolean>}
 */
export const userLogout = async () => {
    authTokenService.removeToken();
    eventService.emit(config.events.EVENT_GARAGE_AUTHENTICATION_STATUS_CHANGE, {
        status: authenticationStatuses.STATUS_NOT_AUTHENTICATED,
    });
    eventService.emit(config.events.EVENT_GARAGE_AUTHENTICATION_LOGOUT);

    return await true;
};

/**
 * Check user authentication status
 *
 * @returns {Promise<boolean>}
 */
export const isUserAuthenticated = async () => {
    const token = await authTokenService.getToken();
    return token !== null;
};

/**
 * Retrieve the user profile information.
 *
 * @param authToken
 * @returns {Promise<*>}
 */
export const userProfile = async (authToken) =>
    request(`${API_URL}/garagek/v1/user/profile`, {
        headers: {
            'DK-Authorization': authToken,
            'Content-Type': 'application/json; charset=UTF-8',
        },
    });

/**
 * Update a user POSTing the relative data to the server.
 *
 * @param email
 * @param name
 * @param surname
 * @param middle_surname
 * @param phone
 * @param zip_code
 * @param address
 * @param street_number
 * @param county
 * @param city
 * @param phone_number
 * @param license_id
 * @param birthday
 * @param authToken
 * @returns {Promise<*>}
 */
export const userUpdate = async (
    {
        email,
        name,
        surname,
        middle_surname,
        phone,
        zip_code,
        street,
        street_number,
        city,
        county,
        phone_number,
        license_id,
        birthday,
    },
    authToken
) => {
    const url = `${API_URL}/garagek/v1/user/profile`;
    const user = {
        email,
        name,
        surname,
        middle_surname,
        phone,
        zip_code,
        street,
        street_number,
        city,
        county,
        phone_number,
        license_id,
        birthday,
    };

    const response = await doRequest(url, {
        method: 'PUT',
        headers: {
            'DK-Authorization': authToken,
            'Content-Type': 'application/json; charset=UTF-8',
        },
        body: JSON.stringify(user),
    });

    const jsonResponse = await response.json();

    eventService.emit(config.events.EVENT_USER_PROFILE_UPDATE, jsonResponse);

    return jsonResponse;
};

/**
 * Update a user privacy POSTing the relative data to the server.
 *
 * @param marketing
 * @param profiling
 * @param third_parties
 * @returns {Promise<*>}
 */
export const userUpdatePrivacy = async ({ marketing, profiling, third_parties }, authToken) => {
    const url = `${API_URL}/garagek/v1/user/privacy`;
    const flags = {
        marketing,
        profiling,
        third_parties,
    };

    const response = await doRequest(url, {
        method: 'PUT',
        headers: {
            'DK-Authorization': authToken,
            'Content-Type': 'application/json; charset=UTF-8',
        },
        body: JSON.stringify(flags),
    });

    const jsonResponse = await response.json();

    eventService.emit(config.events.EVENT_USER_PROFILE_UPDATE, jsonResponse);

    return jsonResponse;
};

/**
 * Recover the user profile
 *
 * @param email
 * @returns {Promise<*>}
 */
export const recoverUser = async (email = '') => {
    const url = `${API_URL}/garagek/v1/user/recover`;

    const response = await doRequest(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json; charset=UTF-8',
        },
        body: JSON.stringify({ email }),
    });

    return await response.json();
};

/**
 * Update the user address.
 *
 * @param params
 * @param authToken
 * @returns {Promise<*>}
 */
export const userAddress = async (params, authToken) => {
    const url = `${API_URL}/garagek/v1/user/address`;

    const response = await doRequest(url, {
        method: 'POST',
        headers: {
            'DK-Authorization': authToken,
            'Content-Type': 'application/json; charset=UTF-8',
        },
        body: JSON.stringify({ ...params }),
    });

    return await response.json();
};
